'use client';

import {
  type ColumnDef,
  flexRender,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  stockFeatures,
  type StockFeatures,
  useTable,
} from '@tanstack/react-table';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import {
  FolderBreadcrumbs,
  type FolderBreadcrumbsProps,
} from '@workspace/ui/components/custom/folder';
import { TablePagination } from '@workspace/ui/components/custom/table-pagination';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { cn } from '@workspace/ui/lib';
import type { TableSelection } from '@workspace/ui/hooks';
import { File, ListFilter, Maximize, Minimize2, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';
import type { ActiveFilter } from './table';

// Stable empty selection so the table state reference is constant when no
// tableSelection is provided.
const EMPTY_ROW_SELECTION: RowSelectionState = {};

/**
 * Props for the DataTable component
 * @template TData - The type of data being displayed in the table
 */
interface DataTableProps<TData extends RowData> {
  /** Array of data items to be displayed in the table */
  data: TData[];

  /** Column definitions for desktop view using TanStack Table's ColumnDef type */
  columns: ColumnDef<StockFeatures, TData>[];

  /** Column definitions for mobile view using TanStack Table's ColumnDef type */
  mobileColumns: ColumnDef<StockFeatures, TData>[];

  /** Pagination state */
  pagination: PaginationState;

  /** Function to set pagination state */
  setPagination: (pagination: PaginationState) => void;

  /** Optional React node containing filter components (search, dropdowns, etc.) */
  filtersComponent?: React.ReactNode;

  /** Optional array of column IDs to hide in tablet view */
  hideColumnsForTablet?: string[];

  /** Optional title to display above the table */
  title?: string;

  /** Optional flag to show loading state with skeletons */
  isLoading?: boolean;

  /** Optional flag to show/hide the expand/collapse button */
  showExpandButton?: boolean;

  /** Optional number of items to display per page in normal view (default: 6) */
  defaultPageSize?: number;

  /** Optional number of items to display per page in expanded view (default: 12) */
  expandedPageSize?: number;

  /** Optional flag to show rows-per-page selector in pagination */
  showPageSizeSelector?: boolean;

  /** Optional rows-per-page options */
  pageSizeOptions?: number[];

  /** Optional label for rows-per-page selector */
  pageSizeSelectorLabel?: string;

  /** Optional total number of items for pagination (useful for server-side pagination) */
  totalItems?: number;

  /** Optional React node containing tab components */
  tabs?: React.ReactNode;

  /** Optional React node for an alert above the table */
  alertSlot?: React.ReactNode;

  /** Optional function to show checkbox in each row */
  showSelect?: boolean;

  /**
   * Controlled, id-based selection bundle from useTableSelection. Required to
   * enable row selection.
   */
  tableSelection?: TableSelection<TData>;

  /** Optional flag to show/hide the upload button */
  uploadButton?: React.ReactNode;

  /** Optional translations */
  t?: (key: string) => string;

  /** Optional className to wrap the table */
  wrapperClassName?: string;

  /** Optional props for the breadcrumb */
  breadcrumbProps?: FolderBreadcrumbsProps;

  /** Optional title to display when the table is empty */
  emptyTableTitle?: string;

  /** Optional description to display when the table is empty */
  emptyTableDescription?: string;

  /** Optional React node for custom search field controlled by parent form */
  searchField?: React.ReactNode;

  /** Optional array of active filters to display as badges */
  activeFilters?: ActiveFilter[];

  /** Optional callback when all filters are cleared */
  onClearAllFilters?: () => void;

  /** Optional callback when apply filters button is clicked */
  onApplyFilters?: () => void;

  /** Optional callback when clear filters button (in popover) is clicked */
  onClearFiltersInPopover?: () => void;

  /** Optional callback when filters popover open state changes */
  onFiltersOpenChange?: (open: boolean) => void;

  /** Optional React node for custom export component */
  exportComponent?: React.ReactNode;
}

export function DocumentsTable<TData extends RowData>({
  data,
  columns,
  mobileColumns,
  filtersComponent,
  hideColumnsForTablet,
  title,
  isLoading = false,
  showExpandButton = false,
  tabs,
  defaultPageSize = 6,
  expandedPageSize = 12,
  showPageSizeSelector = false,
  pageSizeOptions,
  pageSizeSelectorLabel,
  totalItems,
  pagination,
  setPagination,
  alertSlot,
  showSelect = false,
  tableSelection,
  uploadButton,
  wrapperClassName,
  breadcrumbProps,
  t,
  emptyTableTitle,
  emptyTableDescription,
  searchField,
  activeFilters = [],
  onClearAllFilters,
  onApplyFilters,
  onClearFiltersInPopover,
  onFiltersOpenChange,
  exportComponent,
}: DataTableProps<TData>) {
  const tMinimizeTable = t?.('table.minimizeTable') || 'Minimize table';
  const tExpandTable = t?.('table.expandTable') || 'Expand table';

  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const rowSelection = tableSelection?.rowSelection ?? EMPTY_ROW_SELECTION;

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    checkViewport();
  }, []);

  const table = useTable({
    features: stockFeatures,
    data: data || [],
    columns: isMobile ? mobileColumns : columns,
    defaultColumn: {
      size: 20,
      minSize: 5,
      maxSize: 100,
    },
    state: {
      pagination,
      columnVisibility: isTablet
        ? hideColumnsForTablet?.reduce(
            (acc, column) => {
              acc[column] = false;
              return acc;
            },
            {} as Record<string, boolean>
          )
        : undefined,
      rowSelection,
    },
    getRowId: tableSelection?.getRowId,
    manualPagination: true,
    rowCount: totalItems ?? data.length,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
    },
    onRowSelectionChange: tableSelection?.onRowSelectionChange,
  });

  const filterButton = useMemo(() => {
    if (!filtersComponent) return null;

    return (
      <Popover
        open={areFiltersOpen}
        onOpenChange={(open) => {
          setAreFiltersOpen(open);
          onFiltersOpenChange?.(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="secondary"
            className="w-full text-base md:w-[132px] md:min-w-[132px] md:justify-center md:text-sm"
          >
            <ListFilter className="h-4! w-4! stroke-2! md:h-4! md:w-4!" />
            {t?.('table.filter') || 'Filters'}
            {activeFilters && activeFilters.length > 0 && (
              <div className="flex h-4 w-4 min-w-4 items-center justify-center rounded-full bg-black text-xs font-medium leading-none text-white dark:bg-white dark:text-black">
                {activeFilters.length}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align={isMobile ? 'center' : 'end'}
          className="grid w-full grid-cols-1 gap-4 shadow-lg md:w-[573px] md:grid-cols-2 [&_[data-slot='form-item']]:min-w-0 [&_[data-slot='form-item']]:lg:max-w-[253px] [&_[data-slot='form-item']>div:first-child]:lg:max-w-[253px]"
        >
          {filtersComponent}

          <div className="col-span-1 mt-4 flex justify-end gap-2 md:col-span-2">
            <Button
              size="sm"
              variant="secondary"
              className="w-[134px]"
              onClick={(e) => {
                e.preventDefault();
                onClearFiltersInPopover?.();
                setAreFiltersOpen(false);
              }}
            >
              Clear filters
            </Button>

            <Button
              size="sm"
              variant="primary"
              className="w-[134px]"
              onClick={(e) => {
                e.preventDefault();
                onApplyFilters?.();
                setAreFiltersOpen(false);
              }}
            >
              Apply filters
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }, [
    activeFilters,
    areFiltersOpen,
    filtersComponent,
    t,
    isMobile,
    onFiltersOpenChange,
    onClearFiltersInPopover,
    onApplyFilters,
  ]);

  const getExpandButton = useCallback(
    (smallScreen: boolean) => {
      return (
        <Button
          variant={smallScreen ? 'secondary' : 'ghost'}
          className={`w-full items-center justify-center sm:max-w-[134px] sm:min-w-[134px] ${smallScreen ? 'flex sm:hidden' : 'hidden sm:flex'}`}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            setPagination({
              ...pagination,
              pageSize: !isExpanded ? expandedPageSize : defaultPageSize,
              pageIndex: 1,
            });
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          {isExpanded ? tMinimizeTable : tExpandTable}
        </Button>
      );
    },
    [
      isExpanded,
      pagination,
      setPagination,
      expandedPageSize,
      defaultPageSize,
      tMinimizeTable,
      tExpandTable,
    ]
  );

  const throttledResizeHandler = useThrottledCallback(
    useCallback(() => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    }, []),
    1000
  );

  useEffect(() => {
    window.addEventListener('resize', throttledResizeHandler);
    return () => {
      window.removeEventListener('resize', throttledResizeHandler);
    };
  }, [throttledResizeHandler]);

  return (
    <div
      className={cn(
        'bg-grey-25 dark:bg-grey-1000 flex w-full flex-col items-end gap-8 rounded-4xl px-4 py-6 shadow-[0px_429px_120px_rgba(42,43,65,0),_0px_17px_38px_rgba(42,43,65,0.05)] sm:px-8 sm:py-11',
        wrapperClassName
      )}
    >
      <div className="border-b-grey-300 dark:border-b-grey-800 flex w-full flex-col items-center gap-6 border-b pb-8 align-middle sm:gap-8">
        <div
          className={cn(
            'flex w-full flex-col items-start justify-between align-middle sm:flex-row sm:items-center',
            showExpandButton && 'gap-6'
          )}
        >
          <div className="flex w-full flex-col items-start justify-between gap-4 align-middle text-lg leading-normal font-medium text-neutral-200 sm:flex-row sm:items-center dark:text-neutral-100">
            {title}
            {uploadButton && uploadButton}
          </div>
          {showExpandButton && (
            <div className="flex w-full items-center gap-6 sm:w-fit sm:gap-8">
              {getExpandButton(false)}
            </div>
          )}
        </div>
        {tabs && <div className="flex w-full flex-col items-center gap-4 sm:flex-row">{tabs}</div>}

        {(searchField || filtersComponent || exportComponent) && (
          <div className="flex w-full flex-col gap-3">
            <div className="flex w-full flex-col justify-between gap-6 md:flex-row md:gap-4">
              {searchField && <div className="w-full md:flex-grow">{searchField}</div>}
              {exportComponent && exportComponent}
              {filtersComponent && filterButton}
            </div>

            {activeFilters && activeFilters.length > 0 && (
              <div className="flex w-full flex-wrap items-center gap-2">
                {activeFilters.map((filter) => {
                  const displayValue = Array.isArray(filter.value)
                    ? filter.value.join(', ')
                    : filter.value;

                  return (
                    <Badge
                      key={filter.key}
                      variant="filled-grey"
                      size="m"
                      iconAfter={
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            filter.onClear();
                          }}
                          className="text-grey-1100 dark:text-neutral-200"
                          aria-label={`Clear ${filter.label} filter`}
                        >
                          <X className="h-3! w-3!" />
                        </Button>
                      }
                      className="cursor-default"
                    >
                      <span className="text-grey-1100 font-normal">
                        {filter.label}: <span className="font-medium">{displayValue}</span>
                      </span>
                    </Badge>
                  );
                })}

                {onClearAllFilters && activeFilters.length > 1 && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      onClearAllFilters();
                    }}
                    className="text-grey-700 hover:text-grey-900 dark:text-grey-400 dark:hover:text-grey-200 h-auto px-2 py-1 text-xs font-medium"
                  >
                    Clear all
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {showExpandButton && getExpandButton(true)}
        {alertSlot && <div className="w-full">{alertSlot}</div>}
      </div>
      {breadcrumbProps && (
        <FolderBreadcrumbs
          currentFolder={breadcrumbProps.currentFolder}
          setCurrentFolder={breadcrumbProps.setCurrentFolder}
        />
      )}
      <div className={cn('w-full', table.getRowModel()?.rows?.length !== 0 && 'min-h-[457px]')}>
        <div className="flex flex-col gap-5">
          {!isLoading
            ? table.getCoreRowModel().rows.map((row) => (
                <div
                  key={row.id}
                  className={cn(
                    'dark:bg-grey-900 flex h-[64px] flex-nowrap items-center rounded-2xl border-none bg-neutral-100 pl-4 md:pl-6'
                  )}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <React.Fragment key={cell.id}>
                      {index === 0 && showSelect && tableSelection ? (
                        <div
                          className="flex min-w-0 items-center gap-2 sm:gap-8"
                          style={{ flex: `${cell.column.getSize()} 1 0%` }}
                        >
                          <Checkbox
                            id={row.id.toString()}
                            checked={row.getIsSelected()}
                            onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
                            aria-label="Select row"
                          />
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        <div className="min-w-0" style={{ flex: `${cell.column.getSize()} 1 0%` }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ))
            : Array.from({ length: pagination.pageSize }, (_, rowIndex) => (
                <div
                  key={`skeleton-row-${rowIndex}`}
                  className="flex h-[64px] justify-between gap-4 pl-6"
                >
                  {Array.from({ length: 1 }, (_, cellIndex) => (
                    <Skeleton
                      className="bg-grey-200 dark:bg-grey-800 h-full w-full"
                      key={`skeleton-cell-${cellIndex}`}
                    />
                  ))}
                </div>
              ))}
        </div>
      </div>
      {!isLoading && table.getRowModel()?.rows?.length === 0 && (
        <div className="flex h-[457px] w-full flex-col items-center justify-center gap-2">
          <File className="text-grey-400 h-20 w-20 p-4" />
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <span className="text-lg leading-normal font-medium text-neutral-200 dark:text-neutral-100">
              {emptyTableTitle ?? 'No results found'}
            </span>
            <span className="text-grey-700 dark:text-grey-400 max-w-[265px] text-center text-sm leading-none">
              {emptyTableDescription ??
                "Sorry, we couldn't find any results matching your filters."}
            </span>
          </div>
        </div>
      )}
      {(table.getPageCount() > 1 || showPageSizeSelector) &&
        !isLoading &&
        table.getRowModel()?.rows?.length !== 0 && (
          <TablePagination
            className="border-t-grey-300 dark:border-t-grey-800 border-t pt-5"
            totalItems={totalItems ?? table.getRowCount()}
            currentPage={pagination.pageIndex}
            itemsPerPage={pagination.pageSize}
            onPageChange={(page: number) => setPagination({ ...pagination, pageIndex: page })}
            showPageSizeSelector={showPageSizeSelector}
            pageSizeOptions={pageSizeOptions}
            pageSizeSelectorLabel={pageSizeSelectorLabel}
            onPageSizeChange={(pageSize) =>
              setPagination({ ...pagination, pageIndex: 1, pageSize })
            }
          />
        )}
    </div>
  );
}
